import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import {
  useJobsKanban,
  useTransitionStage,
  type Job,
  type JobPriority,
  type KanbanColumn,
} from './hooks/useJobs';

const PRIORITY_VARIANTS: Record<JobPriority, BadgeVariant> = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  urgent: 'danger',
};

/**
 * Kanban board view showing jobs grouped by stage with drag-and-drop transitions.
 * Validates: Requirements 8.8
 */
export function KanbanBoard() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { data: columns, isLoading } = useJobsKanban();
  const transitionStage = useTransitionStage();

  const [draggedJob, setDraggedJob] = useState<Job | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const dragSourceColumn = useRef<string | null>(null);

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, job: Job, sourceColumnId: string) => {
      if (!can(PERMISSIONS.JOBS_TRANSITION)) return;
      setDraggedJob(job);
      dragSourceColumn.current = sourceColumnId;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', job.id);
    },
    [can],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverColumn(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, targetColumnId: string) => {
      e.preventDefault();
      setDragOverColumn(null);

      if (!draggedJob || dragSourceColumn.current === targetColumnId) {
        setDraggedJob(null);
        return;
      }

      transitionStage.mutate({
        jobId: draggedJob.id,
        targetStageId: targetColumnId,
      });

      setDraggedJob(null);
      dragSourceColumn.current = null;
    },
    [draggedJob, transitionStage],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedJob(null);
    setDragOverColumn(null);
    dragSourceColumn.current = null;
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <svg
            className="mx-auto h-8 w-8 animate-spin text-brand-500"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-500">Loading Kanban board...</p>
        </div>
      </div>
    );
  }

  const sortedColumns = [...(columns ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Kanban Board</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Drag and drop jobs between stages to transition them
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/jobs')}>
            List View
          </Button>
          {can(PERMISSIONS.JOBS_CREATE) && (
            <Button onClick={() => navigate('/jobs/new')}>Create Job</Button>
          )}
        </div>
      </div>

      {/* Board */}
      <div className="flex gap-4 overflow-x-auto pb-4" role="region" aria-label="Kanban board">
        {sortedColumns.map((column) => (
          <KanbanColumnComponent
            key={column.stageId}
            column={column}
            isDragOver={dragOverColumn === column.stageId}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            onJobClick={(jobId) => navigate(`/jobs/${jobId}`)}
            canDrag={can(PERMISSIONS.JOBS_TRANSITION)}
          />
        ))}
      </div>
    </div>
  );
}

/** Individual Kanban column */
function KanbanColumnComponent({
  column,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onJobClick,
  canDrag,
}: {
  column: KanbanColumn;
  isDragOver: boolean;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, job: Job, columnId: string) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>, columnId: string) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>, columnId: string) => void;
  onDragEnd: () => void;
  onJobClick: (jobId: string) => void;
  canDrag: boolean;
}) {
  return (
    <div
      className={`flex w-72 flex-shrink-0 flex-col rounded-xl border bg-surface-secondary p-3 transition-colors dark:bg-surface-dark-tertiary ${
        isDragOver
          ? 'border-brand-400 bg-brand-50/50 dark:border-brand-500 dark:bg-brand-900/10'
          : 'border-border dark:border-border-dark'
      }`}
      onDragOver={(e) => onDragOver(e, column.stageId)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, column.stageId)}
      role="group"
      aria-label={`${column.stageName} stage, ${column.jobs.length} jobs`}
    >
      {/* Column header */}
      <div className="mb-3 flex items-center justify-between px-1">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {column.stageName}
        </h3>
        <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-400">
          {column.jobs.length}
        </span>
      </div>

      {/* Cards */}
      <div
        className="flex-1 space-y-2 overflow-y-auto"
        style={{ maxHeight: 'calc(100vh - 240px)' }}
      >
        {column.jobs.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-gray-200 p-4 text-center text-xs text-gray-400 dark:border-gray-700 dark:text-gray-500">
            No jobs in this stage
          </div>
        ) : (
          column.jobs.map((job) => (
            <KanbanCard
              key={job.id}
              job={job}
              columnId={column.stageId}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onClick={() => onJobClick(job.id)}
              canDrag={canDrag}
            />
          ))
        )}
      </div>
    </div>
  );
}

/** Individual Kanban card */
function KanbanCard({
  job,
  columnId,
  onDragStart,
  onDragEnd,
  onClick,
  canDrag,
}: {
  job: Job;
  columnId: string;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, job: Job, columnId: string) => void;
  onDragEnd: () => void;
  onClick: () => void;
  canDrag: boolean;
}) {
  return (
    <div
      draggable={canDrag}
      onDragStart={(e) => onDragStart(e, job, columnId)}
      onDragEnd={onDragEnd}
      className={`cursor-pointer rounded-lg border border-border bg-white p-3 shadow-sm transition-shadow hover:shadow-md dark:border-border-dark dark:bg-surface-dark-secondary ${
        canDrag ? 'cursor-grab active:cursor-grabbing' : ''
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      role="article"
      tabIndex={0}
      aria-label={`Job: ${job.title}, Client: ${job.clientName}`}
    >
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
        {job.title}
      </p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{job.clientName}</p>

      <div className="mt-2 flex items-center justify-between">
        <Badge variant={PRIORITY_VARIANTS[job.priority]} size="sm">
          {job.priority.charAt(0).toUpperCase() + job.priority.slice(1)}
        </Badge>
        {job.isOverdue && (
          <Badge variant="danger" size="sm">
            Overdue
          </Badge>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>{job.assignedAccountantName ?? 'Unassigned'}</span>
        {job.dueDate && (
          <span>
            {new Date(job.dueDate).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
            })}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="mt-2 h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-700">
        <div
          className="h-1.5 rounded-full bg-brand-500 transition-all"
          style={{ width: `${job.progressPercentage}%` }}
        />
      </div>
    </div>
  );
}
